import { useEffect, useState } from 'react'
import AdminLayout from '../components/AdminLayout'
import { AdminTable, Column } from '../components/AdminTable'
import { CalendarDays } from 'lucide-react'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'

type AdminRow = {
  key: string
  sn: number
  name: string
  email: string
  createdAt: string
  createdBy: string
  status: string
  isActive: boolean
}

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'

export default function AdminAdminManagementPage() {
  const [admins, setAdmins] = useState<AdminRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 10

  const loadAdmins = async () => {
    setError(null)
    setLoading(true)
    try {
      const { response } = await adminService.listAdmins({ page: currentPage, limit: itemsPerPage })
      const data: any = (response as any)?.data || response
      const items = data?.items || data?.data?.items || data?.data || data?.admins || []
      setTotalItems(data?.total || data?.data?.total || items.length || 0)
      setAdmins(
        items.map((item: any, idx: number) => ({
          key: item.id || item._id || `${idx}`,
          sn: (currentPage - 1) * itemsPerPage + idx + 1,
          name:
            [item.firstName, item.lastName].filter(Boolean).join(' ') ||
            item.name ||
            item.email ||
            '—',
          email: item.email || '—',
          createdAt: formatDate(item.createdAt),
          createdBy:
            item.createdBy?.name ||
            [item.createdBy?.firstName, item.createdBy?.lastName].filter(Boolean).join(' ') ||
            item.createdBy?.email ||
            '—',
          status: item.isActive ? 'Active' : 'Blocked',
          isActive: Boolean(item.isActive),
        })),
      )
    } catch (err) {
      console.error(err)
      setError(err instanceof ApiError ? err.message : 'Unable to load admins.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAdmins()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const toggleAdminStatus = async (row: AdminRow) => {
    try {
      await adminService.updateAdminStatus(row.key, { isActive: !row.isActive })
      void loadAdmins()
    } catch (err) {
      console.error(err)
      setError(err instanceof ApiError ? err.message : 'Unable to update admin status.')
    }
  }

  const columns: Column<AdminRow>[] = [
    { key: 'sn', label: 'S/N' },
    { key: 'name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'createdBy', label: 'Created By' },
    {
      key: 'status',
      label: 'Status',
      render: row => (
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${
            row.isActive
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : 'bg-red-50 text-red-700 border-red-100'
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <button
          onClick={() => toggleAdminStatus(row)}
          className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'
        >
          {row.isActive ? 'Block' : 'Unblock'}
        </button>
      ),
    },
  ]

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>Hi, Emmanuel</h1>
            <p className='text-sm text-gray-500'>You are welcome</p>
          </div>
          <div className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm text-gray-700 shadow-sm self-start sm:self-auto'>
            <CalendarDays className='h-4 w-4 text-gray-500 flex-shrink-0' />
            <span className='whitespace-nowrap'>15 Jun - 21 Jun 2025</span>
          </div>
        </div>

        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <h2 className='text-lg sm:text-xl font-bold text-gray-900'>Admin Management</h2>
          <button className='rounded-lg bg-[#0b1f3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a1a31] self-start sm:self-auto'>
            Add Admin
          </button>
        </div>

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        <AdminTable<AdminRow>
          columns={columns}
          data={admins}
          loading={loading}
          emptyMessage='No admins found.'
          searchPlaceholder='Search Admin'
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </AdminLayout>
  )
}
